// src/PaymentStateContext.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';

interface PaymentStateProps {
  mid: string;
  sign: string;
  auth: string;
  clientKey: string;
  orderId: string;
  customerId: string;
  productName: string;
  requestCurrency: string;
  requestAmount: number;
  reqDate: string;
  webhookUrl: string;
  returnUrl: string;
  clientIp: string;
  tid: string;
  schemeGroup: string;
  schemeCode: string;
  resultCode?: string;
  resultMessage?: string;
  mallReserved?: string;
  descriptor: string;
  billkeyReq?: string;
  billKey?: string;
  approvalNo?: string;
  payUrl?: string;
  approvalCurrency?: string;
  approvalAmount?: number;
  approvalDay?: string;
  approvalTime?: string;
  responseDateTime?: string;
  appScheme?: string;
  cancelUrl?: string;
  expireDatetime?: string;
}

interface ShopifyStateProps {
  resultStatus: string;
  resultCode: string;
  resultMessage: string;
  tid: string;
  schemeCode: string;
  orderId: string;
  mallReserved: string;
  expireDatetime: string;
  schemeGroup?: string;
  productName?: string;
  requestCurrency?: string;
  requestAmount?: string;
  approvalNo?: string;
  approvalCurrency?: string;
  approvalAmount?: string;
  approvalDay?: string;
  approvalTime?: string;
  billKey?: string;
}

interface PaymentStateContextProps {
  paymentState: PaymentStateProps;
  setPaymentState: React.Dispatch<React.SetStateAction<PaymentStateProps>>;
  shopifyState: ShopifyStateProps;
  setShopifyState: React.Dispatch<React.SetStateAction<ShopifyStateProps>>;
}

const defaultState: PaymentStateProps = {
  mid: '',
  sign: '',
  auth: '',
  clientKey: '',
  orderId: '',
  customerId: '',
  productName: '',
  requestCurrency: '',
  requestAmount: 0,
  reqDate: '',
  clientIp: '',
  webhookUrl: '',
  returnUrl: '',
  tid: '',
  resultCode: '',
  resultMessage: '',
  mallReserved: '',
  descriptor: '',
  billkeyReq: '',
  billKey: '',
  approvalNo: '',
  payUrl: '',
  responseDateTime: '',
  appScheme: '',
  schemeGroup: '',
  schemeCode: '',
  cancelUrl: '',
};

const shopifyDefaultState: ShopifyStateProps = {
  resultStatus: '',
  resultCode: '',
  resultMessage: '',
  tid: '',
  schemeCode: '',
  orderId: '',
  mallReserved: '',
  expireDatetime: '',
};

const PaymentStateContext = createContext<PaymentStateContextProps | undefined>(
  undefined,
);

export const PaymentStateProvider = ({ children }: { children: ReactNode }) => {
  const [paymentState, setPaymentState] =
    useState<PaymentStateProps>(defaultState);
  const [shopifyState, setShopifyState] =
    useState<ShopifyStateProps>(shopifyDefaultState);

  return (
    <PaymentStateContext.Provider
      value={{ paymentState, setPaymentState, shopifyState, setShopifyState }}
    >
      {children}
    </PaymentStateContext.Provider>
  );
};

export const usePaymentState = () => {
  const context = useContext(PaymentStateContext);
  if (context === undefined) {
    throw new Error(
      'usePaymentState must be used within a PaymentStateProvider',
    );
  }
  return context;
};
